/**
 * Role and feature gating for widgets, plus layout sanitising.
 *
 * Gates are opt-in: a flag that is missing from currentOrganization counts as closed
 * (unlike the app's route guard default). Presets drop gated widgets; the gallery shows them
 * locked; a saved layout keeps them in JSON but skips them at render time.
 */
import { WIDGET_BY_ID } from './registry';
import { SIZES } from './sizes';
import { can, isSimplifiedUi } from '../../utils/access';

/**
 * A widget with `perm` is shown when the organization grants that key to the user's role (the
 * key its data endpoint checks). 'employee' widgets belong to the simplified own-work dashboard.
 * The role tiers remain only as a fallback for an entry that forgot its key.
 */
export function roleAllows(entry, organization) {
  if (!entry) return false;
  if (entry.perm) return can(organization, entry.perm);
  if (entry.role === 'all') return true;
  if (entry.role === 'employee') return isSimplifiedUi(organization);
  if (entry.role === 'foreman') return can(organization, 'work_orders.view_all');
  if (entry.role === 'admin') return can(organization, 'team.roles');
  return false;
}

export function gateOpen(entry, organization) {
  if (!entry.gate) return true;
  return Boolean(organization?.[entry.gate]);
}

export function requirementsMet(entry, organization) {
  if (entry.requires === 'employee') {
    // employee_id === null means "confirmed unlinked"; undefined (older payload) counts as linked.
    return organization?.employee_id !== null;
  }
  return true;
}

/** Widgets the user may see in the gallery (role allows; gate may still be closed = locked). */
export function roleVisible(entry, organization) {
  return Boolean(entry && entry.available !== false && roleAllows(entry, organization));
}

export function isWidgetLocked(entry, organization) {
  return !gateOpen(entry, organization) || !requirementsMet(entry, organization);
}

export function isWidgetVisible(entry, organization) {
  return roleVisible(entry, organization) && !isWidgetLocked(entry, organization);
}

/** Drop unknown widgets, coerce unsupported sizes, normalise opts. Never mutates the input. */
export function sanitizeLayout(layout) {
  if (!layout || !Array.isArray(layout.widgets)) return { version: 1, preset: null, widgets: [] };
  const widgets = layout.widgets
    .filter((widget) => widget && WIDGET_BY_ID[widget.id])
    .map((widget) => {
      const entry = WIDGET_BY_ID[widget.id];
      const size = entry.sizes.includes(widget.size) && SIZES[widget.size] ? widget.size : entry.defaultSize;
      const opts = widget.opts && typeof widget.opts === 'object' && !Array.isArray(widget.opts) ? { ...widget.opts } : {};
      return { id: widget.id, size, opts };
    });
  return { version: 1, preset: layout.preset || null, widgets };
}

export function visibleWidgets(layout, organization) {
  return (layout?.widgets || []).filter((widget) => isWidgetVisible(WIDGET_BY_ID[widget.id], organization));
}
